import React from "react";
import { Printer, Package, Layers, Scissors, Flag, ExternalLink } from "lucide-react";
import { useQuote } from "./QuoteDialog";

const PRODUCTS = [
  {
    icon: Printer,
    title: "Poster & Banner Printers",
    body: "Professional wide-format printers designed for schools.",
    accent: "bg-teal/10 text-teal",
  },
  {
    icon: Package,
    title: "Supply Kits",
    body: "Ink, paper, vinyl, and media bundled for real school use.",
    accent: "bg-navy/10 text-navy",
  },
  {
    icon: Layers,
    title: "Laminators",
    body: "Protect and extend the life of posters and signage.",
    accent: "bg-teal/10 text-teal",
  },
  {
    icon: Scissors,
    title: "Vinyl Cutters",
    body: "Create decals, lettering, and custom graphics.",
    accent: "bg-navy/10 text-navy",
  },
  {
    icon: Flag,
    title: "Banner Kits",
    body: "Finish banners in-house with professional tools.",
    accent: "bg-teal/10 text-teal",
  },
];

const PRINTER_IMG =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/booorlhf_P-6409C006AA-2.png";

export default function ProductEcosystem() {
  const { openQuote } = useQuote();

  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative bg-white py-24 md:py-32 border-y border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">
              Product Ecosystem
            </div>
            <h2
              data-testid="products-headline"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-navy leading-tight"
            >
              Everything You Need—In One System
            </h2>
          </div>
          <p className="lg:col-span-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            We don't just sell printers. We provide complete systems so your school can
            create, print, and finish projects in-house.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
          {/* Featured printer card */}
          <div
            data-testid="product-card-printer-feature"
            className="lg:col-span-6 relative overflow-hidden rounded-2xl bg-navy text-white min-h-[420px] group"
          >
            <img
              src={PRINTER_IMG}
              alt="Wide-format poster printer"
              className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-60 transition-opacity duration-500"
              loading="lazy"
                decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/20" />
            <div className="relative h-full flex flex-col justify-end p-8 md:p-10">
              <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center mb-6">
                <Printer className="w-7 h-7 text-white" strokeWidth={2} />
              </div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold mb-3">
                Poster & Banner Printers
              </h3>
              <p className="text-slate-200 max-w-md leading-relaxed">
                Professional wide-format printers designed for schools, ready to handle
                indoor posters and full outdoor banners.
              </p>
            </div>
          </div>

          {/* Other products grid */}
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {PRODUCTS.slice(1).map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`product-card-${i + 1}`}
                  className="group bg-[#F4F6F4] hover:bg-white border border-slate-200 hover:border-teal/40 rounded-2xl p-7 transition-all duration-300 hover:shadow-lg flex flex-col"
                >
                  <div className={`w-12 h-12 rounded-xl ${p.accent} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-6 h-6" strokeWidth={2} />
                  </div>
                  <h3 className="font-serif text-lg md:text-xl font-bold text-navy mb-2">
                    {p.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-14 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <button
            data-testid="products-explore-packages-btn"
            onClick={openQuote}
            title="Opens in a new tab"
            className="group inline-flex items-center justify-center gap-2 bg-navy hover:bg-[#091638] text-white font-bold px-8 py-4 rounded-lg shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5"
          >
            Request a Quote
            <ExternalLink className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <span className="text-sm text-slate-500 italic">
            Tell us your goals—we'll recommend the right setup.
          </span>
        </div>
      </div>
    </section>
  );
}
